/**
 * Cookie / tracking consent — single source of truth.
 *
 * Strictly-necessary storage (auth tokens, theme, view-mode, etc.) is exempt
 * and never gated. Everything that is NOT strictly necessary must wait for an
 * explicit opt-in here before it loads:
 *   - `analytics`  → Amplitude (incl. its session replay) + Sentry session replay
 *
 * The state lives in `localStorage` (not a cookie — we set no first-party
 * tracking cookies ourselves) and is read synchronously so the very first
 * import (Sentry in `instrument.ts`) can decide before React mounts.
 *
 * Non-React consumers (instrument.ts, utils/analytics.ts) subscribe via
 * `subscribeConsent`; the React layer drives writes through `saveConsent`
 * (see context/CookieConsentContext.tsx).
 */

export type CookieCategory = 'necessary' | 'analytics';

export interface CookieConsent {
    analytics: boolean;
    /** Schema version — bump to re-prompt every visitor after a material change. */
    version: number;
    /** ISO timestamp of the decision, for audit/record-keeping. */
    decidedAt: string;
}

/** Bump when the categories or the third parties behind them materially change. */
export const COOKIE_CONSENT_VERSION = 1;

const STORAGE_KEY = 'comtev_cookie_consent';

type Listener = (consent: CookieConsent | null) => void;
const listeners = new Set<Listener>();

/**
 * Read the stored decision. Returns `null` when the visitor has not decided
 * yet, the value is unparseable, or the schema version is stale — all of which
 * mean "treat as no consent and (re-)ask".
 */
export function getStoredConsent(): CookieConsent | null {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<CookieConsent>;
        if (parsed.version !== COOKIE_CONSENT_VERSION) return null;
        return {
            analytics: parsed.analytics === true,
            version: COOKIE_CONSENT_VERSION,
            decidedAt: typeof parsed.decidedAt === 'string' ? parsed.decidedAt : '',
        };
    } catch {
        // Private-mode / disabled storage → behave as undecided.
        return null;
    }
}

/** Persist a decision and notify every subscriber. Returns the saved value. */
export function saveConsent(choice: { analytics: boolean }): CookieConsent {
    const consent: CookieConsent = {
        analytics: choice.analytics,
        version: COOKIE_CONSENT_VERSION,
        decidedAt: new Date().toISOString(),
    };
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
    } catch {
        // If we can't persist we still notify in-memory listeners for this session.
    }
    // Notify each listener in isolation: a third-party SDK gate that throws
    // (e.g. Sentry replay refusing to (re)start) must not abort the remaining
    // listeners or — critically — bubble out of saveConsent and prevent the
    // React layer's setConsent from running, which would leave the banner stuck
    // on screen after the visitor has already decided.
    listeners.forEach((listener) => {
        try {
            listener(consent);
        } catch {
            // Swallow: consent is recorded regardless of an SDK side-effect failing.
        }
    });
    return consent;
}

/**
 * Subscribe to consent changes. The listener fires on every `saveConsent`,
 * never on initial subscription (read `getStoredConsent()` yourself for the
 * current value). Returns an unsubscribe function.
 */
export function subscribeConsent(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

export function hasAnalyticsConsent(): boolean {
    return getStoredConsent()?.analytics === true;
}

export function hasDecided(): boolean {
    return getStoredConsent() !== null;
}
